import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BrandMark } from '@/components/ui/BrandMark';
import { LoaderScreen } from '@/components/ui/LoaderScreen';

export function LoginPage() {
  const { user, loading, error, signIn } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (loading) return <LoaderScreen />;
  if (user) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setLocalError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-bg">
      {/* Left — editorial hero */}
      <aside className="relative hidden lg:flex flex-col justify-between p-12 border-r border-border overflow-hidden">
        {/* Blueprint grid backdrop */}
        <div className="absolute inset-0 bg-blueprint bg-blueprint opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-bg via-bg/80 to-walnut-900/40" />
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-brass-400/[0.03] blur-3xl" />

        <div className="relative z-10">
          <BrandMark />
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="kicker text-brass-300 mb-6">Internal Portal · 2026</div>
          <h1 className="font-display text-6xl xl:text-7xl leading-[0.95] tracking-tight text-text mb-6">
            Architectural
            <br />
            <em className="text-brass-300 font-normal">operations,</em>
            <br />
            under one roof.
          </h1>
          <p className="text-text-muted leading-relaxed max-w-md">
            The command center for Talbert manufacturing — financials, clients, production,
            inventory and logistics across every department, in real time.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
            {[
              ['70+', 'Years'],
              ['181', 'Species'],
              ['65+', 'Team'],
            ].map(([n, l]) => (
              <div key={l} className="bg-bg-panel p-4">
                <div className="font-display text-3xl text-brass-300 tabular">{n}</div>
                <div className="kicker mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-xs text-text-dim">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
            <span className="kicker">FSC® Certified · C014235</span>
          </div>
          <div>City of Industry, CA</div>
        </div>
      </aside>

      {/* Right — sign-in form */}
      <main className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm animate-slide-up">
          <div className="lg:hidden mb-10">
            <BrandMark />
          </div>

          <div className="rule-brass pb-4 mb-8">
            <div className="kicker mb-2">Sign in</div>
            <h2 className="font-display text-3xl text-text leading-none">
              Welcome back.
            </h2>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <Input
              label="Email"
              name="email"
              type="email"
              autoComplete="username"
              required
              placeholder="name@talbert.app"
              leftIcon={<Mail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />

            <Input
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              placeholder="••••••••"
              leftIcon={<Lock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />

            {(localError || error) && (
              <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">
                {localError || error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={submitting}
              rightIcon={<ArrowRight size={16} />}
            >
              Enter Portal
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-border">
            <p className="text-xs text-text-dim leading-relaxed">
              Access to this system is restricted to Talbert Architectural personnel.
              All activity is logged and monitored. Contact an owner or IT
              administrator if you need an account.
            </p>
          </div>

          <footer className="mt-12 flex items-center justify-between text-2xs text-text-dim">
            <span className="kicker">© 2026 Talbert</span>
            <span>v0.1.0</span>
          </footer>
        </div>
      </main>
    </div>
  );
}
